/**
 * applyMainGameRedirect — on the main game domain (helix2000.com),
 * redirect bare / to /resume (which handles both new + returning players:
 * empty roster → CharacterCustomizer, populated → roster picker).
 *
 * Runs as a first-import side effect BEFORE applyModeUrlCoercion and
 * before React mounts, so there's no blank-page flash.
 *
 * Why /resume and not /intro: CharacterSelectScreen auto-switches to the
 * character creator when the roster is empty (CharacterSelectScreen.tsx:48).
 * So /resume handles both cases — no need for a separate server-side
 * character check before redirecting.
 *
 * Functional params (class/map/preset) block this redirect. Since D44
 * (2026-10-09) only the game's own navigations reach it with them (a typed
 * `?map=` goes to `/play` first: runMainGameBoot, oneGameUrl.ts).
 * The `twitch` param is an OAuth callback breadcrumb (?twitch=ok/error)
 * that must be processed at the landing URL — it blocks the redirect so
 * useAuth/AuthScreen can handle it before navigation.
 * Non-functional params (reset, skipSplash, skipLogin) are preserved
 * through the redirect.
 */
import { isMainGameDomain } from '../environment/environment';
import { computePlayRestore, MAIN_GAME_PLAY_FALLBACK, MAIN_GAME_PLAY_PATH } from './mainGamePlayUrl';
import { bootPathname, bootSearch } from './bootUrl';
import { decideOneGameUrl } from './oneGameUrl';
import { consumeInGameNavigation, isTrustedHistoryEntry, TRUSTED_BOOT_STATE_KEY } from './inGameNavigation';
import { captureSeamlessPortalsParam } from '../../game/services/travel/seamlessPortalsFlag';

/**
 * Pure function: compute the redirect URL for the main game domain,
 * or return null if no redirect should happen.
 *
 * Exported for unit testing (jsdom makes window.location.replace
 * non-mockable, so we test the decision logic here instead).
 */
export function computeMainGameRedirect(
  pathname: string,
  search: string,
  isMainGame: boolean,
): string | null {
  if (!isMainGame) return null;
  if (pathname !== '/') return null;

  const sp = new URLSearchParams(search);
  // Functional params (class/map/preset) + OAuth callback breadcrumb
  // (twitch) all block the redirect — the user has a specific intent
  // or is mid-auth-flow.
  const hasBlockingParam =
    sp.has('class') || sp.has('map') || sp.has('preset') || sp.has('twitch');
  if (hasBlockingParam) return null;

  // Preserve non-functional params through the redirect
  const preserveKeys = ['reset', 'skipSplash', 'skipLogin'];
  const newSp = new URLSearchParams();
  for (const key of preserveKeys) {
    const val = sp.get(key);
    if (val !== null) newSp.set(key, val);
  }
  const newSearch = newSp.toString();
  return `/resume${newSearch ? '?' + newSearch : ''}`;
}

export interface MainGameBootDeps {
  pathname: () => string;
  search: () => string;
  history: {
    readonly state: unknown;
    replaceState: (data: unknown, unused: string, url?: string | null) => void;
  };
  /** Leave for another page (window.location.replace). */
  redirect: (url: string) => void;
  /** The game's one-shot "I sent you here" marker (inGameNavigation.ts). */
  consumeInGameNavigation: (pathAndSearch: string) => boolean;
  /** Read prod-settable flags before any parameter is dropped. */
  captureFlags: (params: URLSearchParams) => void;
}

/**
 * The main game domain's boot, in order:
 *  1. capture the flags a playtest may set by URL (lane AU's seamlessPortals);
 *  2. a reload of the clean `/play` restores its boot URL; a fresh `/play`
 *     goes to the roster (mainGamePlayUrl.ts);
 *  3. D44 (oneGameUrl.ts): a URL the game did not send this tab to never picks
 *     what boots — mode paths and `?map=`/mode/QA parameters go to `/play`;
 *  4. bare `/` (the title) goes to the roster.
 * Every boot reader evaluates after this, so none sees a dropped parameter.
 */
export function runMainGameBoot(deps: MainGameBootDeps): void {
  deps.captureFlags(new URLSearchParams(deps.search()));

  const restore = computePlayRestore(deps.pathname(), deps.history.state, true);
  if (restore?.kind === 'redirect') {
    deps.redirect(restore.url);
    return;
  }
  if (restore?.kind === 'restore') {
    // Keep the state (router idx + the stash) so the clean can re-stash it.
    deps.history.replaceState(deps.history.state, '', restore.url);
  }

  const pathAndSearch = `${deps.pathname()}${deps.search()}`;
  const trusted = restore?.kind === 'restore'
    || isTrustedHistoryEntry(deps.history.state, pathAndSearch)
    || deps.consumeInGameNavigation(pathAndSearch);
  const decision = decideOneGameUrl(deps.pathname(), deps.search(), trusted);
  if (decision.kind === 'play') {
    // Rewrite in place first: the rest of this boot (mode coercion, config
    // readers) runs before the page unloads and must see no mode or map.
    deps.history.replaceState(null, '', MAIN_GAME_PLAY_PATH);
    deps.redirect(MAIN_GAME_PLAY_FALLBACK);
    return;
  }
  if (decision.kind === 'strip') {
    deps.history.replaceState(deps.history.state, '', decision.url);
  } else if (trusted) {
    // A reload of this entry, or Back to it, stays the game's own page.
    const state = deps.history.state && typeof deps.history.state === 'object'
      ? (deps.history.state as Record<string, unknown>)
      : {};
    deps.history.replaceState({ ...state, [TRUSTED_BOOT_STATE_KEY]: pathAndSearch }, '');
  }

  const target = computeMainGameRedirect(deps.pathname(), deps.search(), true);
  if (target) deps.redirect(target);
}

// Side effect — runs at module load time, main game domain only.
try {
  if (typeof window !== 'undefined' && isMainGameDomain()) {
    runMainGameBoot({
      pathname: bootPathname,
      search: bootSearch,
      history: window.history,
      redirect: url => window.location.replace(url),
      consumeInGameNavigation: pathAndSearch => consumeInGameNavigation(pathAndSearch),
      captureFlags: params => captureSeamlessPortalsParam(params),
    });
  }
} catch {
  // Best-effort — don't block boot on redirect failure.
}
